
import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus } from 'lucide-react';
import StepIcon from '../StepIcon';
import RecipeImage from '@/components/recipe/RecipeImage';
import StarterRecipesSection from '../StarterRecipesSection';

interface RecipeCollectionStepProps {
  personalRecipeCount: number;
  totalAccessibleRecipes: number;
  isActiveFamily: boolean;
  familyRecipeCount: number;
  latestRecipe?: any; // Recipe object
}

const RecipeCollectionStep: React.FC<RecipeCollectionStepProps> = ({ 
  personalRecipeCount,
  totalAccessibleRecipes,
  isActiveFamily,
  familyRecipeCount,
  latestRecipe
}) => {
  const isComplete = personalRecipeCount > 0; // Complete when user has contributed recipes
  
  const getStepMessage = () => {
    if (isComplete) {
      if (isActiveFamily) {
        return `Great! You've contributed ${personalRecipeCount} recipe${personalRecipeCount !== 1 ? 's' : ''} to your family's collection of ${familyRecipeCount}. Your contributions help make meal planning even better for everyone!`;
      }
      return `Fantastic! You have ${personalRecipeCount} recipe${personalRecipeCount !== 1 ? 's' : ''}!`;
    }
    
    if (isActiveFamily) {
      return `Welcome to your family's recipe collection! Your family already has ${familyRecipeCount} recipe${familyRecipeCount !== 1 ? 's' : ''} to get you started. Add your own favorites to contribute to the family collection and unlock meal planning.`;
    }
    
    return "Start by adding your favorite recipes. You can import from URLs, create quick recipes, or browse our suggestions.";
  };
  
  const getButtonText = () => {
    if (isActiveFamily) {
      return 'Contribute to Family Collection';
    }
    return 'Add Recipes';
  };
  
  const getCompletionMessage = () => {
    if (isActiveFamily) {
      return `${personalRecipeCount} recipe${personalRecipeCount !== 1 ? 's' : ''} contributed to family collection!`;
    }
    return `${personalRecipeCount} recipe${personalRecipeCount !== 1 ? 's' : ''} added!`;
  };
  
  return (
    <Card className={`w-full transition-all duration-300 ${
      isComplete
        ? 'bg-gradient-to-r from-sage/5 to-golden/10 border-sage/20 shadow-md hover:shadow-lg hover:scale-[1.01]'
        : 'bg-white border-border shadow-sm hover:shadow-lg hover:scale-[1.01] hover:border-sage/30'
    }`}>
      <CardHeader className="pb-3 sm:pb-4">
        <div className="flex items-start sm:items-center gap-3 sm:gap-4">
          <StepIcon type="recipe-collection" isComplete={isComplete} isLocked={false} />
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base sm:text-lg font-playfair">
              <span className="leading-tight">
                {isActiveFamily ? 'Join Your Family\'s Recipe Collection' : 'Build Your Recipe Collection'}
              </span>
            </CardTitle>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 pt-0 px-4 sm:px-6">
        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
          {getStepMessage()}
        </p>
        <Link to="/add-recipes">
          <Button className="w-full transition-all duration-200 hover:shadow-md hover:scale-[1.01] bg-sage hover:bg-sage/90">
            <Plus className="h-4 w-4 mr-2" />
            {getButtonText()}
          </Button>
        </Link>
        {isComplete && latestRecipe && (
          <div className="mt-4 pt-4 border-t border-sage/20">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                  <RecipeImage 
                    recipe={latestRecipe} 
                    className="w-full h-full object-cover"
                    size="md"
                    compact={true}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-charcoal leading-tight">
                    {latestRecipe.title}
                  </p>
                </div>
              </div>
              <div className="flex justify-end">
                <Link to="/recipe-collection" className="text-sm font-medium text-sage hover:text-sage/80 transition-colors">
                  View Collection →
                </Link>
              </div>
            </div>
          </div>
        )}
        
        {/* Show starter recipes for non-family users - persist even after adding recipes */}
        {!isActiveFamily && (
          <StarterRecipesSection onRecipeAdded={() => {}} />
        )}
      </CardContent>
    </Card>
  );
};

export default RecipeCollectionStep;
